import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './hooks/useAuth';
import { useGradeSemanal } from './hooks/useGradeSemanal';
import { useMobile } from './hooks/useMobile';
import GlobalStyle from './styles/GlobalStyle';
import { LoginScreen } from './components/auth/LoginScreen';
import { AtivacaoConta } from './components/auth/AtivacaoConta';
import { GradeSemanal } from './components/grade/GradeSemanal';
import { GradeSemanalMobile } from './components/grade/GradeSemanalMobile';
import { PainelAdmin } from './components/admin/PainelAdmin';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { ModalMeusAgendamentos } from './components/common/ModalMeusAgendamentos';
import { ChatBot } from './components/chat/ChatBot';
import { formatDate, parseBackendDate, formatTime } from './utils/formatters';
import { styles, colors } from './styles/globalStyles';
import {
  requestCalendarTokenSilent, requestCalendarToken,
  createCalendarEvent, deleteCalendarEvent,
  generateCalendarLink
} from './utils/googleCalendar';
import { prefetchGoogleClientId } from './utils/googleClientId';
import { aplicarBrandingBasico, hexParaRgb } from './utils/siteBranding';

prefetchGoogleClientId();

// Função auxiliar para formatar data para comparação
const formatarDataParaComparacao = (data) => {
  const d = new Date(data);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Estilos aprimorados
const enhancedStyles = {
  ...styles,
  appContainer: {
    ...styles.appContainer,
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    ...styles.header,
    backgroundColor: 'white',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    padding: '1rem 2rem',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    borderBottom: `1px solid ${colors.grayLight}`
  },
  headerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  headerLogoIcon: {
    fontSize: '2rem',
    background: 'linear-gradient(135deg, var(--admin-header-inicio, #19981b) 0%, var(--admin-header-fim, #106311) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 'bold'
  },
  headerLogoText: {
    fontSize: '1.5rem',
    fontWeight: '700',
    background: 'linear-gradient(135deg, var(--admin-header-inicio, #19981b) 0%, var(--admin-header-fim, #106311) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0
  },
  navTabs: {
    display: 'flex',
    gap: '0.5rem',
    marginLeft: '2rem'
  },
  navTab: {
    padding: '0.75rem 1.5rem',
    borderRadius: '50px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    color: colors.gray
  },
  navTabActive: {
    background: colors.primary,
    color: 'white',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)'
  },
  userCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: `1px solid ${colors.grayLight}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: colors.primary,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    fontSize: '1rem'
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  adminBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },
  professorBadge: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  userBadge: {
    backgroundColor: '#f0f9ff',
    color: '#0369a1'
  },
  mainContainer: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%'
  },
  weekNavigator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${colors.grayLight}`
  },
  weekInfo: {
    textAlign: 'center'
  },
  weekTitle: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: '600',
    color: colors.dark
  },
  weekDates: {
    margin: '0.5rem 0 0 0',
    fontSize: '0.95rem',
    color: colors.gray
  },
  actionButtons: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap'
  },
  buttonPrimary: {
    ...styles.buttonPrimary,
    background: 'linear-gradient(135deg, var(--color-primary, #0f5a30) 0%, var(--color-primary-dark, #106311) 100%)',
    border: 'none',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  buttonOutline: {
    ...styles.buttonOutline,
    borderColor: colors.grayLight,
    fontWeight: '500',
    transition: 'all 0.3s ease'
  },
  mobileMenu: {
    position: 'fixed',
    top: '80px',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    padding: '1.5rem',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 20px 20px',
    zIndex: 999,
    animation: 'slideDown 0.3s ease-out'
  },
  footer: {
    padding: '2rem',
    backgroundColor: colors.dark,
    color: 'white',
    marginTop: '3rem',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px'
  }
};

// CSS com animações e estilos reutilizáveis
const globalCSS = `
:root {
  --color-primary: #0f5a30;
  --color-primary-dark: #106311;
  --color-primary-rgb: 15, 90, 48;
  --color-secondary: #65a24b;
  --color-success: #28A745;
  --color-warning: #FFC107;
  --color-danger: #DC3545;
  --color-info: #17A2B8;
  --color-gray-light: #f1f5f9;
  --color-gray: #64748b;
  --color-dark: #1e293b;
  --color-white: #ffffff;
}

/* Estilos responsivos para Admin */
.admin-mobile-container {
  padding: 12px !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 100vw !important;
  overflow-x: hidden !important;
}

.admin-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  boxShadow: 0 4px 12px rgba(0,0,0,0.05);
  border: 1px solid #e2e8f0;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* Estilos para o aviso de duplicidade */
.vaga-duplicidade-alerta {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  z-index: 9999;
  max-width: 500px;
  width: 90%;
  animation: slideIn 0.3s ease;
  border: 4px solid #fbbf24;
}

.vaga-duplicidade-content {
  text-align: center;
}

.vaga-duplicidade-title {
  font-size: 24px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.vaga-duplicidade-info {
  background: #fffbeb;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  border: 2px solid #fbbf24;
}

.vaga-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #fde68a;
}

.vaga-info-item:last-child {
  border-bottom: none;
}

.vaga-duplicidade-buttons {
  display: flex;
  gap: 12px;
  margin-top: 25px;
}

.btn-confirmar-troca {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}

.btn-confirmar-troca:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(245, 158, 11, 0.3);
}

.btn-cancelar-troca {
  background: transparent;
  color: #64748b;
  border: 2px solid #cbd5e1;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
}

.btn-cancelar-troca:hover {
  border-color: #94a3b8;
  background: #f8fafc;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulseWarning {
  0%, 100% {
    background-color: rgba(251, 191, 36, 0.1);
    border-color: #fbbf24;
  }
  50% {
    background-color: rgba(251, 191, 36, 0.2);
    border-color: #f59e0b;
  }
}

.vaga-aviso-duplicidade {
  animation: pulseWarning 2s infinite;
}

/* Estilo para botão de troca */
.btn-troca-vaga {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
}

.btn-troca-vaga:hover:not(:disabled) {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

/* Modal overlay para o alerta de duplicidade */
.modal-overlay-duplicidade {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9998;
  backdrop-filter: blur(5px);
}
`;

function aplicarSiteConfig(cfg) {
  if (!cfg) return;
  aplicarBrandingBasico(cfg);
  const root = document.documentElement;
  const cores = cfg.cores || {};
  if (cores.primaria) {
    root.style.setProperty('--color-primary', cores.primaria);
    const rgb = hexParaRgb(cores.primaria);
    if (rgb) root.style.setProperty('--color-primary-rgb', rgb);
  }
  if (cores.primaria_escura) root.style.setProperty('--color-primary-dark', cores.primaria_escura);
  if (cores.fundo_app) root.style.setProperty('--color-fundo-app', cores.fundo_app);
  if (cores.header_fundo) root.style.setProperty('--color-header-bg', cores.header_fundo);
  if (cores.admin_header_inicio) root.style.setProperty('--admin-header-inicio', cores.admin_header_inicio);
  if (cores.admin_header_fim) root.style.setProperty('--admin-header-fim', cores.admin_header_fim);
  if (cfg.layout?.cabecalho_fixo === false) root.style.setProperty('--header-position', 'relative');
  else root.style.setProperty('--header-position', 'sticky');
}

function App() {
  const [activeTab, setActiveTab] = useState('grade');
  const [dataReferencia, setDataReferencia] = useState('');
  const [mostrarAtivacao, setMostrarAtivacao] = useState(false);
  const [posicoesNaFila, setPosicoesNaFila] = useState({});
  const [minhasFilas, setMinhasFilas] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mostrarAlertaDuplicidade, setMostrarAlertaDuplicidade] = useState(false);
  const [infoDuplicidade, setInfoDuplicidade] = useState({
    vagaExistente: null,
    novaVaga: null,
    vagaId: null
  });
  const [siteConfig, setSiteConfig] = useState(null);
  const [modalMeusAgendamentos, setModalMeusAgendamentos] = useState(false);

  const [googleCalendarToken, setGoogleCalendarToken] = useState(() => {
    const token = localStorage.getItem('calendarAccessToken');
    const expiry = localStorage.getItem('calendarTokenExpiry');
    if (token && expiry && Date.now() < parseInt(expiry, 10)) return token;
    localStorage.removeItem('calendarAccessToken');
    localStorage.removeItem('calendarTokenExpiry');
    return null;
  });
  const [calNotification, setCalNotification] = useState(null);
  const { user, loading, error, loadUserData, logout, createApi } = useAuth();
  const { grade, periodo, loading: gradeLoading, refetch: refetchGrade } = useGradeSemanal(user, createApi, dataReferencia);
  const isMobile = useMobile();

  // Carrega configurações visuais do site no startup
  useEffect(() => {
    fetch('/api/site/configuracoes')
      .then(r => r.json())
      .then(data => {
        if (data.configuracoes && Object.keys(data.configuracoes).length > 0) {
          setSiteConfig(data.configuracoes);
          aplicarSiteConfig(data.configuracoes);
        }
      })
      .catch(() => {});
  }, []);

  // Ouve atualizações de personalização feitas pelo admin em tempo real
  useEffect(() => {
    const handler = (e) => {
      setSiteConfig(e.detail);
      aplicarSiteConfig(e.detail);
    };
    window.addEventListener('siteConfigUpdate', handler);
    return () => window.removeEventListener('siteConfigUpdate', handler);
  }, []);


  // Tenta token silencioso ao logar — apenas se o admin habilitou modo OAuth
  useEffect(() => {
    if (user?.googleId && !googleCalendarToken && siteConfig?.calendario?.modo === 'oauth') {
      requestCalendarTokenSilent().then(token => {
        if (token) {
          setGoogleCalendarToken(token);
          localStorage.setItem('calendarAccessToken', token);
          localStorage.setItem('calendarTokenExpiry', String(Date.now() + 3600 * 1000));
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, siteConfig]);

  // Auto-dismiss calendar notification after 12s
  useEffect(() => {
    if (!calNotification) return;
    const t = setTimeout(() => setCalNotification(null), 12000);
    return () => clearTimeout(t);
  }, [calNotification]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setMostrarAtivacao(true);
    }
  }, []);

  const handleLoginSuccess = useCallback((credential, isLocal = false) => {
    if (credential && loadUserData) {
      loadUserData(credential, isLocal);
    }
  }, [loadUserData]);

  const handleAtivacaoSuccess = () => {
    setMostrarAtivacao(false);
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleCancelarAtivacao = () => {
    setMostrarAtivacao(false);
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleRefresh = () => {
    refetchGrade();
    if (isMobile) {
      alert('Grade atualizada! ✅');
    }
  };

  const navegarSemana = (dias) => {
    const isAdmin = user?.is_admin || user?.is_professor;
    let dataBase = dataReferencia ? new Date(dataReferencia) : new Date();
    const novaData = new Date(dataBase);
    novaData.setDate(dataBase.getDate() + dias);

    if (!isAdmin) {
      const vis = siteConfig?.visualizacao;
      const semAnt = vis?.semanas_anteriores ?? 0;
      const semFut = vis?.semanas_futuras ?? 8;

      const hoje = new Date();
      const diaSemana = hoje.getDay();
      const diff = diaSemana === 0 ? -6 : 1 - diaSemana;
      const inicioSemanaAtual = new Date(hoje);
      inicioSemanaAtual.setDate(hoje.getDate() + diff);
      inicioSemanaAtual.setHours(0, 0, 0, 0);

      if (semAnt >= 0) {
        const limiteAnterior = new Date(inicioSemanaAtual);
        limiteAnterior.setDate(inicioSemanaAtual.getDate() - semAnt * 7);
        if (novaData < limiteAnterior) return;
      }

      if (semFut > 0) {
        const limiteFuturo = new Date(inicioSemanaAtual);
        limiteFuturo.setDate(inicioSemanaAtual.getDate() + semFut * 7);
        if (novaData >= limiteFuturo) return;
      }
    }

    const dataFormatada = novaData.toISOString().split('T')[0];
    setDataReferencia(dataFormatada);
  };

  useEffect(() => {
    if (!grade || !user) return;
    const vagasOcupadas = grade
      .filter(v => v.agendamento_id && v.aluno_google_id !== user.googleId)
      .map(v => v.id);

    if (vagasOcupadas.length === 0) {
      setPosicoesNaFila({});
      return;
    }

    const api = createApi();
    api.get(`/waitlist/posicoes?vagas=${vagasOcupadas.join(',')}`)
      .then(res => setPosicoesNaFila(res.data.posicoes || {}))
      .catch(err => console.warn('Erro ao carregar posições na fila:', err));
  }, [grade, user, createApi]);

  useEffect(() => {
    if (!user || user.is_admin || user.is_professor) return;
    const api = createApi();
    api.get('/waitlist/minhas')
      .then(res => setMinhasFilas(res.data.filas || []))
      .catch(() => {});
  }, [grade, user, createApi]);

  // FUNÇÃO PRINCIPAL DE AGENDAMENTO COM VERIFICAÇÃO DE DUPLICIDADE
  const handleAgendar = async (vagaId) => {
    if (!vagaId) return;

    // ── Parte SÍNCRONA (contexto de gesto do usuário preservado) ──────────
    const vagaParaAgendar = grade?.find(v => v.id === vagaId);
    if (!vagaParaAgendar) { alert('Vaga não encontrada'); return; }

    const dataVagaParaAgendar = parseBackendDate(vagaParaAgendar.data_hora);
    const dataFormatadaParaAgendar = formatarDataParaComparacao(dataVagaParaAgendar);

    const vagasAgendadasDoUsuario = grade?.filter(v => {
      if (!v.agendamento_id) return false;
      const isMyBooking = (user.googleId && v.aluno_google_id === user.googleId) ||
                         (user.id && v.aluno_id && v.aluno_id === user.id);
      if (!isMyBooking) return false;
      const dataFormatadaVaga = formatarDataParaComparacao(parseBackendDate(v.data_hora));
      return dataFormatadaVaga === dataFormatadaParaAgendar;
    });

    if (vagasAgendadasDoUsuario?.length > 0) {
      setInfoDuplicidade({ vagaExistente: vagasAgendadasDoUsuario[0], novaVaga: vagaParaAgendar, vagaId });
      setMostrarAlertaDuplicidade(true);
      return;
    }

    const modoOAuth = siteConfig?.calendario?.modo === 'oauth';

    // ── OAuth: solicita token antes do primeiro await (contexto do clique) ─
    let calToken = null;
    if (modoOAuth) {
      calToken = googleCalendarToken;
      if (!calToken && user?.googleId) {
        calToken = await requestCalendarToken();
        if (calToken) {
          setGoogleCalendarToken(calToken);
          localStorage.setItem('calendarAccessToken', calToken);
          localStorage.setItem('calendarTokenExpiry', String(Date.now() + 3600 * 1000));
        }
      }
    }
    // ── Fim parte síncrona ─────────────────────────────────────────────────

    try {
      const api = createApi();
      const response = await api.post('/agendamento', { vagaId });
      const agendamentoId = response.data.agendamento_id;
      refetchGrade();

      if (modoOAuth && calToken) {
        const eventId = await createCalendarEvent(calToken, vagaParaAgendar, user, siteConfig?.cabecalho?.titulo);
        if (eventId && agendamentoId) {
          localStorage.setItem(`cal_event_${agendamentoId}`, eventId);
        }
        setCalNotification({
          message: eventId
            ? 'Agendado! Aula salva no seu Google Agenda ✓'
            : 'Agendado! Google Agenda da academia atualizado ✓',
          calendarLink: !eventId ? generateCalendarLink(vagaParaAgendar, siteConfig?.cabecalho?.titulo) : null,
          vaga: !eventId ? vagaParaAgendar : null
        });
      } else {
        setCalNotification({
          message: 'Agendado! Google Agenda da academia atualizado ✓',
          calendarLink: generateCalendarLink(vagaParaAgendar, siteConfig?.cabecalho?.titulo),
          vaga: vagaParaAgendar
        });
      }

    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao realizar agendamento';
      alert('❌ ' + errorMessage);
    }
  };

  // FUNÇÃO PARA CONFIRMAR TROCA DE VAGA
  const confirmarTrocaVaga = async () => {
    const { vagaExistente, novaVaga, vagaId } = infoDuplicidade;

    const modoOAuth = siteConfig?.calendario?.modo === 'oauth';
    let calToken = null;
    if (modoOAuth) {
      calToken = googleCalendarToken;
      if (!calToken && user?.googleId) {
        calToken = await requestCalendarToken();
        if (calToken) {
          setGoogleCalendarToken(calToken);
          localStorage.setItem('calendarAccessToken', calToken);
          localStorage.setItem('calendarTokenExpiry', String(Date.now() + 3600 * 1000));
        }
      }
    }

    try {
      const api = createApi();

      await api.delete(`/agendamento/${vagaExistente.agendamento_id}`);
      if (calToken) {
        const oldEventId = localStorage.getItem(`cal_event_${vagaExistente.agendamento_id}`);
        if (oldEventId) {
          deleteCalendarEvent(calToken, oldEventId);
          localStorage.removeItem(`cal_event_${vagaExistente.agendamento_id}`);
        }
      }

      const response = await api.post('/agendamento', { vagaId });
      const agendamentoId = response.data.agendamento_id;
      refetchGrade();
      setMostrarAlertaDuplicidade(false);
      setInfoDuplicidade({ vagaExistente: null, novaVaga: null, vagaId: null });

      if (calToken) {
        const eventId = await createCalendarEvent(calToken, novaVaga, user, siteConfig?.cabecalho?.titulo);
        if (eventId && agendamentoId) localStorage.setItem(`cal_event_${agendamentoId}`, eventId);
        setCalNotification({
          message: eventId ? 'Vaga trocada! Seu Google Agenda atualizado ✓' : 'Vaga trocada! Google Agenda da academia atualizado ✓',
          calendarLink: !eventId ? generateCalendarLink(novaVaga, siteConfig?.cabecalho?.titulo) : null,
          vaga: !eventId ? novaVaga : null
        });
      } else {
        setCalNotification({
          message: 'Vaga trocada! Google Agenda da academia atualizado ✓',
          calendarLink: generateCalendarLink(novaVaga, siteConfig?.cabecalho?.titulo),
          vaga: novaVaga
        });
      }

    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao atualizar agendamento';
      alert('❌ ' + errorMessage);
      setMostrarAlertaDuplicidade(false);
    }
  };

  // FUNÇÃO PARA CANCELAR TROCA DE VAGA
  const cancelarTrocaVaga = () => {
    setMostrarAlertaDuplicidade(false);
    setInfoDuplicidade({ vagaExistente: null, novaVaga: null, vagaId: null });
  };

  const handleDesmarcar = async (agendamentoId) => {
    if (!window.confirm('Tem certeza que deseja cancelar este agendamento?')) return;
    try {
      const api = createApi();
      const response = await api.delete(`/agendamento/${agendamentoId}`);
      refetchGrade();
      let calSyncMsg = '';
      if (siteConfig?.calendario?.modo === 'oauth' && googleCalendarToken) {
        const eventId = localStorage.getItem(`cal_event_${agendamentoId}`);
        if (eventId) {
          deleteCalendarEvent(googleCalendarToken, eventId);
          localStorage.removeItem(`cal_event_${agendamentoId}`);
          calSyncMsg = ' Seu Google Agenda atualizado ✓';
        }
      }
      setCalNotification({
        message: (response.data.message || 'Agendamento cancelado!') + calSyncMsg
      });
    } catch (err) {
      let errorMessage = 'Erro ao cancelar agendamento';
      
      if (err.response?.status === 400) {
        const serverError = err.response.data.error || err.response.data.message;
        
        if (serverError.includes('antecedência') || serverError.includes('tempo mínimo')) {
          errorMessage = '❌ Não é possível cancelar este agendamento.\n\n';
          errorMessage += '**Regra violada:** ' + serverError + '\n\n';
          errorMessage += 'Por favor, entre em contato com a administração se precisar cancelar com urgência.';
        } else if (serverError.includes('horário limite')) {
          errorMessage = '❌ Cancelamento não permitido.\n\n';
          errorMessage += '**Motivo:** O horário limite para cancelamentos já passou.\n\n';
          errorMessage += 'Consulte as regras de cancelamento na página de políticas.';
        } else if (serverError.includes('tentativas')) {
          errorMessage = '❌ Limite de tentativas excedido.\n\n';
          errorMessage += '**Motivo:** Você já tentou cancelar muitas vezes hoje.\n\n';
          errorMessage += 'Por favor, tente novamente amanhã ou entre em contato com o suporte.';
        } else {
          errorMessage = '❌ ' + serverError;
        }
      } else if (err.response?.status === 403) {
        errorMessage = '❌ Acesso negado.\n\n';
        errorMessage += 'Você não tem permissão para cancelar este agendamento.';
      } else if (err.response?.status === 404) {
        errorMessage = '❌ Agendamento não encontrado.\n\n';
        errorMessage += 'Este agendamento pode já ter sido cancelado ou não existe mais.';
      } else if (err.response?.status === 500) {
        errorMessage = '❌ Erro interno do servidor.\n\n';
        errorMessage += 'Por favor, tente novamente mais tarde ou entre em contato com o suporte.';
      } else if (err.message) {
        errorMessage = '❌ ' + err.message;
      }
      
      alert(errorMessage);
    }
  };

  const handleSolicitarWaitlist = async (vagaId, liberarAgendamentoId) => {
    try {
      const api = createApi();
      const response = await api.post('/waitlist', { vagaId, ...(liberarAgendamentoId ? { liberarAgendamentoId } : {}) });
      alert('✅ ' + (response.data.message || 'Você entrou na lista de espera!'));
      refetchGrade();
      api.get('/waitlist/minhas').then(r => setMinhasFilas(r.data.filas || [])).catch(() => {});
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Erro ao solicitar lista de espera';
      alert('❌ ' + errorMessage);
    }
  };

  const sairDaWaitlist = async (vagaId) => {
    if (!window.confirm('Deseja sair da lista de espera para esta vaga?')) return;
    try {
      const api = createApi();
      await api.delete(`/waitlist/${vagaId}`);
      alert('✅ Você saiu da lista de espera.');
      refetchGrade();
      api.get('/waitlist/minhas').then(r => setMinhasFilas(r.data.filas || [])).catch(() => {});
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || 'Erro ao sair da fila'));
    }
  };

  useEffect(() => {
    // Correções específicas para mobile
    if (isMobile && activeTab === 'admin') {
      // Forçar recálculo de layouts
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      
      // Corrigir overflow
      document.body.style.overflowX = 'hidden';
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflowX = '';
        document.body.style.width = '';
      };
    }
  }, [isMobile, activeTab]);

  // Obter vagas do usuário para passar para os componentes
  const vagasDoUsuario = (grade && user) ? grade.filter(v => {
    const isMyBooking = (user.googleId && v.aluno_google_id === user.googleId) ||
                       (user.id && v.aluno_id && v.aluno_id === user.id);
    return isMyBooking;
  }) : [];

  if (mostrarAtivacao) {
    return (
      <>
        <GlobalStyle />
        <AtivacaoConta onSuccess={handleAtivacaoSuccess} onCancel={handleCancelarAtivacao} />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <LoadingSpinner size="lg" message="Carregando aplicação..." />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <GlobalStyle />
        <LoginScreen onSuccess={handleLoginSuccess} error={error} />
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <style>{globalCSS}</style>
      
      {/* Modal de Alerta para Duplicidade */}
      {mostrarAlertaDuplicidade && infoDuplicidade.vagaExistente && infoDuplicidade.novaVaga && (
        <>
          <div className="modal-overlay-duplicidade" onClick={cancelarTrocaVaga}></div>
          <div className="vaga-duplicidade-alerta">
            <div className="vaga-duplicidade-content">
              <h2 className="vaga-duplicidade-title">
                <span>⚠️</span>
                Você já possui uma vaga neste dia!
              </h2>
              
              <div className="vaga-duplicidade-info">
                <div className="vaga-info-item">
                  <span style={{ fontWeight: '600', color: '#92400e' }}>Vaga Atual:</span>
                  <span>
                    {formatDate(parseBackendDate(infoDuplicidade.vagaExistente.data_hora))} às{' '}
                    {formatTime(parseBackendDate(infoDuplicidade.vagaExistente.data_hora))}
                  </span>
                </div>
                
                <div className="vaga-info-item">
                  <span style={{ fontWeight: '600', color: '#059669' }}>Nova Vaga:</span>
                  <span>
                    {formatDate(parseBackendDate(infoDuplicidade.novaVaga.data_hora))} às{' '}
                    {formatTime(parseBackendDate(infoDuplicidade.novaVaga.data_hora))}
                  </span>
                </div>
                
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#92400e',
                  textAlign: 'left'
                }}>
                  <strong>⚠️ Atenção:</strong> Ao confirmar, sua vaga atual será automaticamente cancelada e substituída pela nova vaga.
                </div>
              </div>
              
              <div className="vaga-duplicidade-buttons">
                <button className="btn-cancelar-troca" onClick={cancelarTrocaVaga}>
                  Manter vaga atual
                </button>
                <button className="btn-confirmar-troca" onClick={confirmarTrocaVaga}>
                  Trocar para nova vaga
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      <div style={{
        ...enhancedStyles.appContainer,
        padding: isMobile ? '0' : '0',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}>
        {/* Header Responsivo */}
        <header style={{
          ...enhancedStyles.header,
          flexDirection: isMobile ? 'column' : 'row',
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          gap: isMobile ? '1rem' : '0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}>
            <div style={enhancedStyles.headerLogo}>
              {siteConfig?.cabecalho?.logo_url
                ? <img src={siteConfig.cabecalho.logo_url} alt={siteConfig?.cabecalho?.titulo || 'Logo'} style={{ height: isMobile ? '28px' : '34px', maxWidth: '160px', objectFit: 'contain' }} />
                : <span style={enhancedStyles.headerLogoIcon}>{siteConfig?.cabecalho?.icone || '📅'}</span>}
              <h1 style={{
                ...enhancedStyles.headerLogoText,
                fontSize: isMobile ? '1.25rem' : '1.5rem'
              }}>{siteConfig?.cabecalho?.titulo || 'Minha Agenda'}</h1>
            </div>
            {!isMobile && (
              <div style={enhancedStyles.navTabs}>
                <button
                  onClick={() => setActiveTab('grade')}
                  style={{
                    ...enhancedStyles.navTab,
                    ...(activeTab === 'grade' && enhancedStyles.navTabActive)
                  }}
                >
                  📅 Grade Semanal
                </button>
                {(user.is_admin || user.is_professor) && (
                  <button
                    onClick={() => setActiveTab('admin')}
                    style={{
                      ...enhancedStyles.navTab,
                      ...(activeTab === 'admin' && enhancedStyles.navTabActive)
                    }}
                  >
                    ⚙ Painel Admin
                  </button>
                )}
              </div>
            )}
            {!isMobile && (
              <div style={enhancedStyles.userCard}>
                <div style={enhancedStyles.avatar}>
                  {user.picture
                    ? <img src={user.picture} alt={user.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                    : user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                    {user.name}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <span style={{
                      ...enhancedStyles.badge,
                      ...(user.is_admin ? enhancedStyles.adminBadge : user.is_professor ? enhancedStyles.professorBadge : enhancedStyles.userBadge)
                    }}>
                      {user.is_admin ? 'Administrador' : user.is_professor ? 'Professor' : 'Aluno'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={logout}
                  style={{
                    ...enhancedStyles.buttonOutline,
                    padding: '0.5rem 1rem',
                    marginLeft: '1rem'
                  }}
                >
                  Sair
                </button>
              </div>
            )}
            {isMobile && (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: colors.primary
                }}
              >
                {isMenuOpen ? '✕' : '☰'}
              </button>
            )}
          </div>
          {/* Overlay para fechar menu mobile */}
          {isMobile && isMenuOpen && (
            <div
              onClick={() => setIsMenuOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.3)',
                zIndex: 998
              }}
            />
          )}
          {/* Menu Mobile */}
          {isMobile && isMenuOpen && (
            <div style={{
              ...enhancedStyles.mobileMenu,
              top: '70px',
              zIndex: 999
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  onClick={() => {
                    setActiveTab('grade');
                    setIsMenuOpen(false);
                  }}
                  style={{
                    ...enhancedStyles.navTab,
                    ...(activeTab === 'grade' && enhancedStyles.navTabActive),
                    width: '100%',
                    textAlign: 'left',
                    padding: '1rem'
                  }}
                >
                  📅 Grade Semanal
                </button>
                {(user.is_admin || user.is_professor) && (
                  <button
                    onClick={() => {
                      setActiveTab('admin');
                      setIsMenuOpen(false);
                    }}
                    style={{
                      ...enhancedStyles.navTab,
                      ...(activeTab === 'admin' && enhancedStyles.navTabActive),
                      width: '100%',
                      textAlign: 'left',
                      padding: '1rem'
                    }}
                  >
                    ⚙ {user.is_admin ? 'Painel Admin' : 'Painel Professor'}
                  </button>
                )}
                <div style={{
                  borderTop: `1px solid ${colors.grayLight}`,
                  marginTop: '1rem',
                  paddingTop: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={enhancedStyles.avatar}>
                      {user.picture
                        ? <img src={user.picture} alt={user.name} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                        : user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600' }}>{user.name}</div>
                      <div style={{
                        ...enhancedStyles.badge,
                        ...(user.is_admin ? enhancedStyles.adminBadge : user.is_professor ? enhancedStyles.professorBadge : enhancedStyles.userBadge),
                        display: 'inline-block',
                        marginTop: '0.25rem'
                      }}>
                        {user.is_admin ? 'Administrador' : user.is_professor ? 'Professor' : 'Aluno'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    style={{
                      ...enhancedStyles.buttonOutline,
                      width: '100%',
                      padding: '0.75rem'
                    }}
                  >
                    Sair da Conta
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main style={{
          ...enhancedStyles.mainContainer,
          padding: isMobile ? '1rem' : '2rem',
          maxWidth: siteConfig?.layout?.desktop_max_width || '100%',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {activeTab === 'grade' && (
            <div style={{ width: '100%' }}>
              {/* Banner de rascunhos para admin */}
              {(user.is_admin || user.is_professor) && grade && grade.filter(v => v.rascunho).length > 0 && (
                <div style={{
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '12px',
                  padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>
                    <span>📋</span>
                    <span style={{ color: '#1e40af', fontWeight: '500' }}>
                      Esta semana tem <strong>{grade.filter(v => v.rascunho).length} rascunho(s)</strong> não publicados — alunos não os veem.
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveTab('admin')}
                    style={{
                      background: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.4rem 0.9rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Ir para Admin ⚙
                  </button>
                </div>
              )}
              {/* Navegação de Semana */}
              {!isMobile ? (
                <div style={enhancedStyles.weekNavigator}>
                  <button
                    onClick={() => navegarSemana(-7)}
                    style={enhancedStyles.buttonOutline}
                  >
                    ◀ Semana Anterior
                  </button>
                  <div style={enhancedStyles.weekInfo}>
                    <h2 style={enhancedStyles.weekTitle}>Agenda Semanal</h2>
                    {periodo && (
                      <p style={enhancedStyles.weekDates}>
                        {formatDate(periodo.inicio)} - {formatDate(new Date(periodo.fim.getTime() - 1))}
                      </p>
                    )}
                  </div>
                  <div style={enhancedStyles.actionButtons}>
                    <button
                      onClick={() => setDataReferencia('')}
                      style={{
                        ...enhancedStyles.buttonPrimary,
                        animation: dataReferencia ? 'pulse 2s infinite' : 'none'
                      }}
                    >
                      Hoje
                    </button>
                    <button
                      onClick={() => navegarSemana(7)}
                      style={enhancedStyles.buttonOutline}
                    >
                      Próxima Semana ▶
                    </button>
                    <button
                      onClick={handleRefresh}
                      style={enhancedStyles.buttonOutline}
                      title="Atualizar grade"
                    >
                      ↻ Atualizar
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                  border: `1px solid ${colors.grayLight}`,
                  padding: '1.25rem 1rem',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {/* Título e período */}
                  <div style={{ textAlign: 'center' }}>
                    <h2 style={{
                      margin: 0,
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: colors.dark,
                      letterSpacing: '-0.01em'
                    }}>
                      Agenda Semanal
                    </h2>
                    {periodo && (
                      <p style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.8rem',
                        color: colors.gray,
                        fontWeight: '500'
                      }}>
                        {formatDate(periodo.inicio)} — {formatDate(new Date(periodo.fim.getTime() - 1))}
                      </p>
                    )}
                  </div>

                  {/* Botões de navegação */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gap: '8px',
                    alignItems: 'stretch'
                  }}>
                    <button
                      onClick={() => navegarSemana(-7)}
                      style={{
                        background: 'white',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '10px 12px',
                        fontSize: '0.82rem',
                        fontWeight: '600',
                        color: '#475569',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                        minHeight: '44px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ← Anterior
                    </button>

                    <button
                      onClick={() => setDataReferencia('')}
                      style={{
                        background: dataReferencia
                          ? 'linear-gradient(135deg, var(--admin-header-inicio, #19981b) 0%, var(--admin-header-fim, #106311) 100%)'
                          : '#f1f5f9',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 18px',
                        fontSize: '0.82rem',
                        fontWeight: '700',
                        color: dataReferencia ? 'white' : '#94a3b8',
                        cursor: 'pointer',
                        minHeight: '44px',
                        whiteSpace: 'nowrap',
                        boxShadow: dataReferencia
                          ? '0 4px 12px rgba(var(--color-primary-rgb, 15, 90, 48), 0.25)'
                          : 'none',
                        transition: 'all 0.2s ease',
                        letterSpacing: '0.02em'
                      }}
                    >
                      Hoje
                    </button>

                    <button
                      onClick={() => navegarSemana(7)}
                      style={{
                        background: 'white',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '10px 12px',
                        fontSize: '0.82rem',
                        fontWeight: '600',
                        color: '#475569',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                        minHeight: '44px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Próxima →
                    </button>
                  </div>

                  {/* Botão atualizar */}
                  <button
                    onClick={handleRefresh}
                    style={{
                      background: 'none',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '8px',
                      fontSize: '0.78rem',
                      fontWeight: '500',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      width: '100%',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ↻ Atualizar Grade
                  </button>
                </div>
              )}

              {/* Banner Meus Agendamentos */}
              <div
                onClick={() => setModalMeusAgendamentos(true)}
                style={{
                  background: 'linear-gradient(135deg, var(--color-primary, #0f5a30) 0%, var(--color-primary-dark, #106311) 100%)',
                  borderRadius: isMobile ? 14 : 16,
                  padding: isMobile ? '1rem 1.25rem' : '1.1rem 1.75rem',
                  marginBottom: isMobile ? '1rem' : '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '0.875rem' : '1.25rem',
                  cursor: 'pointer',
                  boxShadow: '0 8px 28px rgba(var(--color-primary-rgb, 15,90,48),0.3)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  userSelect: 'none'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 36px rgba(var(--color-primary-rgb, 15,90,48),0.38)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(var(--color-primary-rgb, 15,90,48),0.3)';
                }}
              >
                {/* Ícone */}
                <div style={{
                  width: isMobile ? 44 : 52,
                  height: isMobile ? 44 : 52,
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '1.4rem' : '1.6rem',
                  flexShrink: 0
                }}>
                  🗓️
                </div>

                {/* Texto */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: isMobile ? '0.95rem' : '1.05rem',
                    letterSpacing: '-0.01em'
                  }}>
                    Meus Agendamentos
                  </div>
                  <div style={{
                    color: 'rgba(255,255,255,0.78)',
                    fontSize: isMobile ? '0.775rem' : '0.825rem',
                    marginTop: 2
                  }}>
                    {vagasDoUsuario.length > 0
                      ? `${vagasDoUsuario.length} aula${vagasDoUsuario.length !== 1 ? 's' : ''} agendada${vagasDoUsuario.length !== 1 ? 's' : ''} esta semana`
                      : 'Visualize e cancele suas aulas agendadas'
                    }
                  </div>
                </div>

                {/* Badge + CTA */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexShrink: 0
                }}>
                  {vagasDoUsuario.length > 0 && (
                    <div style={{
                      backgroundColor: 'white',
                      color: 'var(--color-primary, #0f5a30)',
                      borderRadius: '50%',
                      width: isMobile ? 26 : 30,
                      height: isMobile ? 26 : 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      fontWeight: 800
                    }}>
                      {vagasDoUsuario.length}
                    </div>
                  )}
                  <div style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: 10,
                    padding: isMobile ? '0.35rem 0.7rem' : '0.4rem 1rem',
                    color: 'white',
                    fontSize: isMobile ? '0.775rem' : '0.825rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}>
                    Ver todos →
                  </div>
                </div>
              </div>

              {/* Banner: filas de espera ativas */}
              {!user?.is_admin && !user?.is_professor && minhasFilas.length > 0 && (
                <div style={{
                  background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                  border: '1.5px solid #c4b5fd',
                  borderRadius: 14,
                  padding: isMobile ? '14px 16px' : '16px 20px',
                  marginBottom: 16,
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: minhasFilas.length > 0 ? 12 : 0
                  }}>
                    <div style={{
                      width: 36, height: 36,
                      backgroundColor: '#8b5cf6',
                      borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.1rem', flexShrink: 0
                    }}>⏳</div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#5b21b6', fontSize: isMobile ? '0.95rem' : '1rem' }}>
                        Fila de espera ativa
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#7c3aed' }}>
                        {minhasFilas.length === 1 ? 'Você está aguardando 1 horário' : `Você está aguardando ${minhasFilas.length} horários`}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {minhasFilas.map(fila => {
                      const dtFila = new Date(fila.data_hora);
                      const dataStr = dtFila.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
                      const horaStr = dtFila.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                      const dtLiberar = fila.data_hora_liberar ? new Date(fila.data_hora_liberar) : null;
                      const horaLiberar = dtLiberar ? dtLiberar.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;

                      return (
                        <div key={fila.id} style={{
                          backgroundColor: 'white',
                          borderRadius: 10,
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          flexWrap: isMobile ? 'wrap' : 'nowrap'
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
                            }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                backgroundColor: '#f5f3ff', border: '1px solid #ddd6fe',
                                borderRadius: 8, padding: '3px 10px',
                                fontSize: '0.875rem', fontWeight: 700, color: '#7c3aed'
                              }}>
                                🎯 {fila.posicao}º na fila
                              </span>
                              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>
                                {dataStr} às {horaStr}
                              </span>
                              {fila.descricao && (
                                <span style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>
                                  {fila.descricao}
                                </span>
                              )}
                            </div>
                            {horaLiberar && (
                              <div style={{
                                marginTop: 4, fontSize: '0.78rem', color: '#7c3aed',
                                display: 'flex', alignItems: 'center', gap: 4
                              }}>
                                <span>⚡</span>
                                <span>Sua vaga das <strong>{horaLiberar}</strong> será trocada automaticamente ao conseguir este horário</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => sairDaWaitlist(fila.vaga_id)}
                            style={{
                              background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 8,
                              padding: '5px 12px', fontSize: '0.8rem', fontWeight: 600,
                              color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0
                            }}
                          >
                            Sair da fila
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Grade de Horários */}
              {gradeLoading ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '4rem',
                  width: '100%'
                }}>
                  <LoadingSpinner />
                </div>
              ) : isMobile ? (
                <div style={{ width: '100%' }}>
                  <GradeSemanalMobile
                    grade={grade}
                    user={user}
                    onAgendar={handleAgendar}
                    onDesmarcar={handleDesmarcar}
                    onSolicitarWaitlist={handleSolicitarWaitlist}
                    onSairDaWaitlist={sairDaWaitlist}
                    isAdminView={false}
                    naFilaMap={posicoesNaFila}
                    minhasFilas={minhasFilas}
                    userVagasNoDia={vagasDoUsuario}
                    onRefresh={refetchGrade}
                    visualizacaoConfig={(!user?.is_admin && !user?.is_professor) ? siteConfig?.visualizacao : null}
                  />
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                  overflow: 'hidden',
                  width: '100%'
                }}>
                  <GradeSemanal
                    grade={grade}
                    user={user}
                    onAgendar={handleAgendar}
                    onDesmarcar={handleDesmarcar}
                    onSolicitarWaitlist={handleSolicitarWaitlist}
                    onSairDaWaitlist={sairDaWaitlist}
                    isAdminView={false}
                    naFilaMap={posicoesNaFila}
                    minhasFilas={minhasFilas}
                    userVagasNoDia={vagasDoUsuario}
                    onRefresh={refetchGrade}
                    visualizacaoConfig={(!user?.is_admin && !user?.is_professor) ? siteConfig?.visualizacao : null}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'admin' && (user.is_admin || user.is_professor) && (
            <div className="admin-mobile-container" style={{ width: '100%' }}>
              <PainelAdmin
                grade={grade}
                user={user}
                createApi={createApi}
                onRefresh={handleRefresh}
                periodo={periodo}
                dataReferencia={dataReferencia}
                setDataReferencia={setDataReferencia}
              />
            </div>
          )}
        </main>

        {/* Notificação de agendamento com link Google Calendar */}
        {calNotification && (
          <div style={{
            position: 'fixed',
            bottom: 90,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'white',
            borderRadius: 14,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            padding: '1rem 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            zIndex: 9998,
            maxWidth: 420,
            width: 'calc(100vw - 2rem)',
            border: '1px solid #dcfce7',
            animation: 'fadeIn 0.3s ease'
          }}>
            <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>✅</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#166534', fontSize: '0.9rem', marginBottom: '0.35rem' }}>
                {calNotification.message}
              </div>
              {calNotification.calendarLink && (
                <a
                  href={calNotification.calendarLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    backgroundColor: '#4285f4',
                    color: 'white',
                    padding: '0.3rem 0.75rem',
                    borderRadius: 20,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: 'none'
                  }}
                >
                  📆 Adicionar ao Google Agenda
                </a>
              )}
            </div>
            <button
              onClick={() => setCalNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '1.1rem',
                padding: 0,
                flexShrink: 0
              }}
            >✕</button>
          </div>
        )}

        {/* Modal Meus Agendamentos */}
        <ModalMeusAgendamentos
          isOpen={modalMeusAgendamentos}
          onClose={() => setModalMeusAgendamentos(false)}
          createApi={createApi}
          onCancelado={refetchGrade}
        />

        {/* Chatbot IA */}
        <ChatBot createApi={createApi} />

        {/* Footer */}
        <footer style={{
          ...enhancedStyles.footer,
          padding: isMobile ? '1.5rem 1rem' : '2rem',
          marginTop: isMobile ? '2rem' : '3rem'
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '1.5rem' : '0',
            width: '100%'
          }}>
            <div>
              <div style={{
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                fontWeight: '600',
                marginBottom: '0.5rem',
                background: 'linear-gradient(135deg, var(--color-primary, #65a24b) 0%, var(--color-primary-dark, #407e1f) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {siteConfig?.cabecalho?.titulo || 'Minha Agenda'}
              </div>
              <div style={{ fontSize: isMobile ? '0.8rem' : '0.9rem', opacity: 0.8 }}>
                {siteConfig?.cabecalho?.subtitulo || 'Sistema de Agendamento Online'}
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: isMobile ? '1rem' : '1.5rem',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center'
            }}>
              {siteConfig?.contato?.email_suporte && (
              <button
                type="button"
                onClick={() => window.open(`mailto:${siteConfig.contato.email_suporte}`, '_blank')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  opacity: 0.8,
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  padding: 0,
                  textAlign: isMobile ? 'left' : 'center',
                  transition: 'opacity 0.3s ease'
                }}
              >
                📞 Suporte
              </button>
              )}
              <button
                type="button"
                onClick={() => window.open('/privacidade.html', '_blank')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  opacity: 0.8,
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  padding: 0,
                  textAlign: isMobile ? 'left' : 'center',
                  transition: 'opacity 0.3s ease'
                }}>
                📄 Políticas
              </button>
              <button
                type="button"
                onClick={() => window.open('/termos.html', '_blank')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  opacity: 0.8,
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  padding: 0,
                  textAlign: isMobile ? 'left' : 'center',
                  transition: 'opacity 0.3s ease'
                }}>
                ⚖ Termos
              </button>
            </div>
            <div style={{ fontSize: isMobile ? '0.8rem' : '0.85rem', opacity: 0.7 }}>
              © {new Date().getFullYear()} {siteConfig?.cabecalho?.titulo || 'Minha Agenda'} • v1.2.0
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default App;
